import Entry from 'src/clientRoutes/login'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/login'

export const handle = {
  name: 'login',
  track: { scene: 'login' },
  // Tell the root layout in `src/root.tsx` to render `<Outlet />` directly
  // instead of suspending behind `<RootRuntime>` (the client-only B-class
  // provider tree). The login page body — `pages/SignPage.tsx` — only uses
  // SSR-safe primitives (Tailwind, react-router hooks, useTranslation,
  // JsonLd) and gates the client-only Sign form behind `<ClientOnly
  // fallback={<FormSkeleton />}>`, so the real DOM (h1, headlines,
  // showcase grid, FAQ section, JsonLd) lands in the first SSR flush
  // and crawlers see the full SEO content. Without this flag the root
  // shell renders `null` for top-level routes and SSR emits an empty
  // `<!--$--><!--/$-->` placeholder where the page should be.
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

/** SSR loader. Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L73-L78 */
export function loader({ request, context }: Route.LoaderArgs) {
  const { t } = getI18n(context)
  return staticMetaLoader({
    requestUrl: request.url,
    title: t('server:meta.login.title'),
    description: t('server:meta.login.description'),
  })
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

/**
 * Dual-mode entry. Both `'off'` and `'on'` render the exact same
 * `<SignPage isLogin />` tree — the auth page has no SSR-specific
 * data prefetch, so the only material difference between the two
 * branches is whether the SSR-shell branch in `root.tsx` is taken
 * (`'on'` → SSR-flushed h1 / FAQ / JsonLd / showcase; `'off'` →
 * `<RootRuntime>` Suspense fallback that renders the form on the
 * client).
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <Entry />,
  on: () => <Entry />,
})
